import { useOfficeComponentContext } from '@office-start/component-context-service';
import { ContentActionGroup } from '@office-start/document-types';
import { useIdentity } from '@office-start/identity-service';
import { useLocStrings } from '@office-start/loc-service';
import { useIsDarkMode } from '@office-start/theming-service';
import { useMemo } from 'react';
import { getActionTelemetryConfig } from './../actionTelemetry';
import { buildDocumentActions } from './../util';
import { getActionsGroupInfo } from './../util/systemActionHelper';
import { useActions } from './actionManagerHooks';
/**
 * @description A wrapper to return document actions from the content actions returned from @office-start/actions-service
 * @returns an IDocumentActionsCreator object that can be used to create actions for the given item
 *
 */
export var useDocumentActionsCreator = function () {
    // Used for aria label and menu title
    var locStrings = useLocStrings().data;
    // Used for Share action currently
    var componentContext = useOfficeComponentContext().data;
    var identity = useIdentity().data;
    var isDarkMode = useIsDarkMode();
    var _a = useActions(), actionExecutor = _a.actionExecutor, actionsEnabled = _a.actionsEnabled, actionManager = _a.actionManager, contentActions = _a.contentActions;
    return useMemo(function () {
        if (!componentContext || !identity || !locStrings || !actionManager) {
            return undefined;
        }
        return {
            // Only allow custom action execution when contentActionsEnabled is true
            actionExecutor: actionsEnabled ? actionExecutor : undefined,
            createDocumentActions: function (content, index, targetUX, sourceUXComponent, itemCategory) {
                // Use @office-start/document-actions-service powered actions if feature not enabled.
                if (!actionsEnabled) {
                    return actionManager.createDefaultDocumentActions({
                        document: content,
                        index: index,
                        targetElementId: targetUX.targetElementId,
                        menuResources: locStrings.documentActionsMenuResources,
                        componentContext: componentContext,
                        identity: identity,
                        isDarkMode: isDarkMode,
                    });
                }
                // Wait for all content actions to be registered and for a way to execute them...
                if (!contentActions || !actionExecutor) {
                    return [];
                }
                var telemetryConfig = getActionTelemetryConfig('content', content, sourceUXComponent, itemCategory, 'ContentActionsMenuItem');
                // Build a menu 🍔
                return buildDocumentActions(contentActions, actionExecutor, content, telemetryConfig, targetUX, sourceUXComponent);
            },
            createCommandActions: function (document, _index, targetUX, sourceUXComponent, itemCategory) {
                if (!actionsEnabled || !contentActions || !actionExecutor) {
                    return [];
                }
                return buildDocumentActions(
                // Share is the only supported command action
                contentActions.filter(function (action) {
                    return action.metadata.groupId ===
                        ContentActionGroup.Share;
                }), actionExecutor, document, getActionTelemetryConfig('content', document, sourceUXComponent, itemCategory, 'ContentActionsCommandMenuItem'), targetUX, sourceUXComponent);
            },
            getContentActionsGroupInfo: function () {
                if (!actionsEnabled) {
                    return undefined;
                }
                return getActionsGroupInfo(locStrings);
            },
        };
    }, [
        actionExecutor,
        actionManager,
        actionsEnabled,
        contentActions,
        componentContext,
        locStrings,
        identity,
        isDarkMode,
    ]);
};
//# sourceMappingURL=useDocumentActionsCreator.js.map